<template>
  <div class="space-y-2 flex flex-col justify-between h-full">
    <div class="basis-full">
      <label for="create-room-name" class="block text-sm font-medium text-text-secondary mb-1">Room Name (Optional)</label>
      <input
        id="create-room-name"
        v-model="roomName"
        name="roomName"
        type="text"
        placeholder="e.g., Drum Class 101"
        class="w-full bg-background border border-text-secondary/20 rounded-xl px-4 py-3 text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
        @keyup.enter="handleCreate"
      />
    </div>

    <button
      class="w-full bg-primary text-text-primary rounded-xl py-3 px-4 font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      :disabled="loading"
      @click="handleCreate"
    >
      <span v-if="loading" class="flex items-center justify-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Creating...
      </span>
      <span v-else>Create Room</span>
    </button>

    <p v-if="error" class="text-error text-sm text-center">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useMultiplayerRoom } from '@/composables/useMultiplayerRoom';
import { useToast } from '@/composables/useToast';

const emit = defineEmits<{
  (e: 'created', roomCode: string): void;
}>();

const { createRoom } = useMultiplayerRoom();
const { createToast } = useToast();

const roomName = ref('');
const loading = ref(false);
const error = ref('');

const handleCreate = async () => {
  loading.value = true;
  error.value = '';

  try {
    const code = await createRoom({
      name: roomName.value || undefined,
    });

    createToast({
      type: 'success',
      message: `Room created! Code: ${code}`,
    });

    emit('created', code);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to create room';
    createToast({
      type: 'error',
      message: error.value,
    });
  } finally {
    loading.value = false;
  }
};
</script>
